import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  type Severity,
  type FindingType,
  type CanonicalStatus,
  normalizeCanonicalStatus,
} from "@/components/finding-badges";
import { Eye, UserCheck, Camera } from "lucide-react";
import { PersonaRow } from "@/components/experience/persona-row";
import { Timestamp } from "@/components/ui/timestamp";
import { FindingKicker } from "@/components/experience/finding-kicker";
import { FindingPolarityMark } from "@/components/experience/finding-polarity-mark";
import {
  StepScreenshot,
  type TargetBox,
} from "@/components/walkthrough/step-screenshot";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { markdownPreview } from "@/lib/plain-text";
import { cn } from "@/lib/utils";

// ── Types ─────────────────────────────────────────────────────

export type FindingVariant = "full" | "compact" | "pill" | "inline";

export interface FindingRowData {
  id: string;
  title?: string | null;
  description: string;
  what?: string | null;
  why?: string | null;
  breaksRule?: string | null;
  breaksFile?: string | null;
  screen?: string | null;
  type: FindingType | null;
  severity: Severity | null;
  complianceImpact?: string | null;
  fixStatus?: string | null;
  /** Canonical finding status (open/addressed/dismissed) */
  status?: CanonicalStatus | null;
  /** Canonical lifecycle state; supersedes `status` for the chip */
  state?: string | null;
  stateReason?: string | null;
  /** Number of observations (canonical findings) */
  observationCount?: number;
  /** Observations in the Findings scope's period (null/absent without one). */
  periodObservationCount?: number | null;
  /** Persona name for inline display */
  personaName?: string | null;
  /** Scenario the finding came from (raw findings) */
  scenarioName?: string | null;
  /** Lens label */
  lens?: string | null;
  /** First seen timestamp */
  firstSeenAt?: string | Date | null;
  /** Last seen timestamp (canonical findings) */
  lastSeenAt?: string | Date | null;
  /** Optional step number where it was observed */
  stepNumber?: number | null;
  /** Optional walkthrough ID for visual evidence preview */
  walkthroughId?: string | null;
  /** Optional target bounding box for visual highlight */
  targetBox?: TargetBox | null;
}

export interface FindingRowProps {
  finding: FindingRowData;
  /** Layout variant: full (queue/persona), compact (side panels/overview), pill (timeline), inline (prose) */
  variant?: FindingVariant;
  /** Primary target link for the finding title */
  href?: string | null;
  /** Custom selection or click handler */
  onSelect?: (findingId: string) => void;
  /** Actions rendered to the right of the content */
  actions?: ReactNode;
  /** Trailing accessory element (e.g. priority chip, thumbnail, badge) */
  trailing?: ReactNode;
  /** Leading element (e.g., selection checkbox) */
  leading?: ReactNode;
  /** Contextual badge (used in pill variant) */
  badge?: ReactNode;
  /** Custom subtitle/kicker line for compact variant */
  subtitle?: ReactNode;
  /** Whether the finding is visually muted (e.g., dismissed) */
  muted?: boolean;
  /** Heading level for the title — match the surrounding outline */
  headingLevel?: "h2" | "h3" | "h4" | "span";
  /** Leading chip (the queue's priority chip), before severity. */
  priority?: ReactNode;
  /** Extra chips after the built-in ones (judge, later: assignee, links). */
  meta?: ReactNode;
  /** Who owns the work (pillar D); no chip when absent or unassigned. */
  assignee?: { displayName: string | null } | null;
  className?: string;
}

// ── Component ────────────────────────────────────────────────

/**
 * FindingRow (also aliased as FindingItem) — standard representation of a finding.
 * Supports 4 canonical variants: "full", "compact", "pill", and "inline".
 */
export function FindingRow({
  finding,
  variant = "full",
  href,
  onSelect,
  actions,
  trailing,
  leading,
  badge,
  subtitle,
  muted,
  headingLevel: Heading = "h3",
  priority,
  meta,
  assignee,
  className,
}: FindingRowProps) {
  const effectiveType = finding.type ?? "hinders";
  const effectiveSeverity = finding.severity ?? "nit";
  const obsCount = finding.observationCount ?? 1;
  const status = finding.status
    ? normalizeCanonicalStatus(finding.status)
    : null;

  const title = finding.title ?? null;
  // Bodies are agent-written markdown; the row shows a one-line preview.
  const description = markdownPreview(
    finding.description || finding.what || finding.why || "",
  );
  const lens = finding.lens && finding.lens !== "persona" ? finding.lens : null;

  // ── Variant: Inline (Prose / Reasoning Text) ─────────────────
  if (variant === "inline") {
    const text = `“${title || description}”`;
    if (href) {
      return (
        <Link
          to={href}
          onClick={(e) => {
            if (
              onSelect &&
              !e.metaKey &&
              !e.ctrlKey &&
              !e.shiftKey &&
              !e.altKey &&
              e.button === 0
            ) {
              e.preventDefault();
              onSelect(finding.id);
            }
          }}
          className={cn(
            "underline decoration-border underline-offset-4 hover:decoration-foreground font-medium text-foreground",
            className,
          )}
          title={title || description}
        >
          {text}
        </Link>
      );
    }
    if (onSelect) {
      return (
        <button
          type="button"
          onClick={() => onSelect(finding.id)}
          className={cn(
            "underline decoration-border underline-offset-4 hover:decoration-foreground font-medium text-foreground cursor-pointer",
            className,
          )}
          title={title || description}
        >
          {text}
        </button>
      );
    }
    return (
      <span
        className={cn("font-medium text-foreground", className)}
        title={title || description}
      >
        {text}
      </span>
    );
  }

  // ── Variant: Pill (Execution Trace Timeline) ─────────────────
  if (variant === "pill") {
    const titleText = title || description;
    return (
      <div
        className={cn(
          "group inline-flex max-w-full items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/20 px-2.5 py-1 text-xs transition-colors hover:border-border hover:bg-muted/40",
          className,
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <FindingPolarityMark
            type={effectiveType}
            severity={effectiveSeverity}
            className="h-3 self-center"
          />
          {href ? (
            <Link
              to={href}
              onClick={(e) => {
                if (
                  onSelect &&
                  !e.metaKey &&
                  !e.ctrlKey &&
                  !e.shiftKey &&
                  !e.altKey &&
                  e.button === 0
                ) {
                  e.preventDefault();
                  onSelect(finding.id);
                }
              }}
              className="truncate font-medium text-foreground hover:underline"
              title={titleText}
            >
              {titleText}
            </Link>
          ) : onSelect ? (
            <button
              type="button"
              onClick={() => onSelect(finding.id)}
              className="truncate text-left font-medium text-foreground hover:underline cursor-pointer"
              title={titleText}
            >
              {titleText}
            </button>
          ) : (
            <span
              className="truncate font-medium text-foreground"
              title={titleText}
            >
              {titleText}
            </span>
          )}
        </div>
        {badge && <div className="shrink-0">{badge}</div>}
        {trailing && <div className="shrink-0">{trailing}</div>}
      </div>
    );
  }

  // ── Variant: Compact (Secondary Lists, Hotspots, Buckets, Overview) ───
  if (variant === "compact") {
    const titleText = title || description;
    return (
      <div
        className={cn(
          "group flex min-w-0 flex-1 items-stretch gap-3 py-1",
          className,
        )}
      >
        <FindingPolarityMark
          type={effectiveType}
          severity={effectiveSeverity}
        />

        <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex items-center gap-2">
              {leading}
              <Heading
                className={cn(
                  "min-w-0 truncate text-sm font-semibold leading-tight",
                  muted
                    ? "text-muted-foreground line-through"
                    : "text-foreground",
                )}
                title={titleText}
              >
                {href ? (
                  <Link
                    to={href}
                    aria-label={`Open finding: ${titleText}`}
                    onClick={(e) => {
                      if (
                        onSelect &&
                        !e.metaKey &&
                        !e.ctrlKey &&
                        !e.shiftKey &&
                        !e.altKey &&
                        e.button === 0
                      ) {
                        e.preventDefault();
                        onSelect(finding.id);
                      }
                    }}
                    className="inline-flex items-center min-h-6 hover:underline underline-offset-4 decoration-border focus-visible:outline-2 focus-visible:outline-ring rounded-xs"
                  >
                    {titleText}
                  </Link>
                ) : onSelect ? (
                  <button
                    type="button"
                    aria-label={`Open finding: ${titleText}`}
                    onClick={() => onSelect(finding.id)}
                    className="inline-flex items-center min-h-6 hover:underline underline-offset-4 decoration-border text-left cursor-pointer focus-visible:outline-2 focus-visible:outline-ring rounded-xs"
                  >
                    {titleText}
                  </button>
                ) : (
                  titleText
                )}
              </Heading>
            </div>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              {subtitle ?? (
                <>
                  <FindingKicker
                    severity={effectiveSeverity}
                    type={effectiveType}
                    state={finding.state}
                    stateReason={finding.stateReason}
                    status={status}
                    fixStatus={finding.fixStatus}
                    leading={priority}
                  />
                  {finding.personaName && (
                    <PersonaRow
                      variant="inline"
                      persona={{
                        id: finding.personaName,
                        name: finding.personaName,
                      }}
                      size="xs"
                    />
                  )}
                  {finding.scenarioName && (
                    <span
                      className="max-w-[24ch] truncate"
                      title={finding.scenarioName}
                    >
                      · {finding.scenarioName}
                    </span>
                  )}
                  {finding.breaksRule && (
                    <span
                      className="max-w-[32ch] truncate text-foreground"
                      title={
                        finding.breaksFile
                          ? `Breaks rule "${finding.breaksRule}" (${finding.breaksFile})`
                          : `Breaks rule "${finding.breaksRule}"`
                      }
                    >
                      · Breaks “{finding.breaksRule}”
                      {finding.breaksFile ? ` (${finding.breaksFile})` : ""}
                    </span>
                  )}
                </>
              )}
              {meta}
            </div>
          </div>

          {(trailing || actions) && (
            <div className="flex shrink-0 items-center gap-2">
              {trailing}
              {actions}
            </div>
          )}
        </div>
      </div>
    );
  }

  const hasPersonaOrScenario = Boolean(
    finding.personaName || finding.scenarioName,
  );
  const hasStepPopover = Boolean(
    finding.walkthroughId && typeof finding.stepNumber === "number",
  );
  const hasInlineStep = Boolean(
    !finding.walkthroughId && typeof finding.stepNumber === "number" && !meta,
  );
  const hasAnyStep = hasStepPopover || hasInlineStep;
  const hasScreen = Boolean(finding.screen);

  // ── Variant: Full (Default: Triage Queue & Primary Lists) ───────
  return (
    <div className={cn("flex min-w-0 flex-1 items-stretch gap-3", className)}>
      {/* Polarity mark — hue = polarity, intensity = severity */}
      <FindingPolarityMark type={effectiveType} severity={effectiveSeverity} />

      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 flex-1 space-y-1.5 py-0.5">
          <div className="flex items-center gap-2">
            {leading}
            <Heading
              className={cn(
                "min-w-0 line-clamp-2 text-base font-semibold leading-snug",
                muted
                  ? "text-muted-foreground line-through"
                  : "text-foreground",
              )}
              title={title || description}
            >
              {href ? (
                <Link
                  to={href}
                  aria-label={`Open finding: ${title || description}`}
                  onClick={(e) => {
                    if (
                      onSelect &&
                      !e.metaKey &&
                      !e.ctrlKey &&
                      !e.shiftKey &&
                      !e.altKey &&
                      e.button === 0
                    ) {
                      e.preventDefault();
                      onSelect(finding.id);
                    }
                  }}
                  className="hover:underline underline-offset-4 decoration-border focus-visible:outline-2 focus-visible:outline-ring rounded-xs"
                >
                  {title || description}
                </Link>
              ) : onSelect ? (
                <button
                  type="button"
                  aria-label={`Open finding: ${title || description}`}
                  onClick={() => onSelect(finding.id)}
                  className="hover:underline underline-offset-4 decoration-border text-left cursor-pointer focus-visible:outline-2 focus-visible:outline-ring rounded-xs"
                >
                  {title || description}
                </button>
              ) : (
                title || description
              )}
            </Heading>
          </div>

          {title && description && (
            <p
              className="max-w-[80ch] truncate text-sm text-muted-foreground"
              title={description}
            >
              {description}
            </p>
          )}

          {/* Status signal chips */}
          <FindingKicker
            severity={effectiveSeverity}
            type={effectiveType}
            state={finding.state}
            stateReason={finding.stateReason}
            status={status}
            fixStatus={finding.fixStatus}
            leading={priority}
          />

          {/* Provenance & context strip */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground pt-0.5">
            {finding.personaName && (
              <PersonaRow
                variant="inline"
                persona={{
                  id: finding.personaName,
                  name: finding.personaName,
                }}
                size="xs"
              />
            )}
            {finding.scenarioName && (
              <>
                {finding.personaName && <span aria-hidden="true">·</span>}
                <span
                  className="max-w-[28ch] truncate"
                  title={finding.scenarioName}
                >
                  {finding.scenarioName}
                </span>
              </>
            )}
            {finding.screen && (
              <>
                {hasPersonaOrScenario && <span aria-hidden="true">·</span>}
                <span className="font-mono text-muted-foreground">
                  {finding.screen}
                </span>
              </>
            )}
            {finding.walkthroughId &&
              typeof finding.stepNumber === "number" && (
                <>
                  {(hasPersonaOrScenario || hasScreen) && (
                    <span aria-hidden="true">·</span>
                  )}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                        title={
                          finding.targetBox
                            ? `Visual evidence with target area from Step ${finding.stepNumber}`
                            : `Visual evidence from Step ${finding.stepNumber}`
                        }
                      >
                        <Camera
                          className="size-3 shrink-0"
                          aria-hidden="true"
                        />
                        <span>Step {finding.stepNumber}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-80 p-2 sm:w-96"
                      side="top"
                      align="start"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                          <span className="font-medium text-foreground">
                            Step {finding.stepNumber} evidence
                          </span>
                          {finding.targetBox && (
                            <span
                              className={cn(
                                "rounded px-1.5 py-0.5 text-xs font-medium",
                                finding.type === "helps"
                                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                  : "bg-destructive/10 text-destructive",
                              )}
                            >
                              Target area marked
                            </span>
                          )}
                        </div>
                        <div className="aspect-video w-full overflow-hidden rounded border border-border bg-black/5">
                          <StepScreenshot
                            walkthroughId={finding.walkthroughId}
                            stepNumber={finding.stepNumber}
                            targetBox={finding.targetBox}
                            targetTone={finding.severity || finding.type}
                            alt={`Step ${finding.stepNumber} screenshot`}
                          />
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </>
              )}
            {!finding.walkthroughId &&
              typeof finding.stepNumber === "number" &&
              !meta && (
                <>
                  {(hasPersonaOrScenario || hasScreen) && (
                    <span aria-hidden="true">·</span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <Camera className="size-3" aria-hidden="true" />
                    Step {finding.stepNumber}
                  </span>
                </>
              )}
            {lens && (
              <>
                {(hasPersonaOrScenario || hasScreen || hasAnyStep) && (
                  <span aria-hidden="true">·</span>
                )}
                <span>{lens.replace("_", " ")}</span>
              </>
            )}
            {finding.breaksRule && (
              <>
                {(hasPersonaOrScenario ||
                  hasScreen ||
                  hasAnyStep ||
                  Boolean(lens)) && <span aria-hidden="true">·</span>}
                <span
                  className="inline-flex items-center gap-1.5 text-foreground"
                  title={
                    finding.breaksFile
                      ? `Breaks rule "${finding.breaksRule}" (${finding.breaksFile})`
                      : `Breaks rule "${finding.breaksRule}"`
                  }
                >
                  <span className="max-w-[36ch] truncate font-medium">
                    Breaks “{finding.breaksRule}”
                  </span>
                  {finding.breaksFile && (
                    <span className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-muted-foreground">
                      {finding.breaksFile}
                    </span>
                  )}
                </span>
              </>
            )}
            {assignee && (
              <>
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1 font-medium text-foreground">
                  <UserCheck
                    className="size-3 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span>{assignee.displayName ?? "a member"}</span>
                </span>
              </>
            )}
            {meta && (
              <>
                {(hasPersonaOrScenario ||
                  hasScreen ||
                  hasAnyStep ||
                  Boolean(lens) ||
                  Boolean(finding.breaksRule) ||
                  Boolean(assignee)) && <span aria-hidden="true">·</span>}
                {meta}
              </>
            )}
            {typeof finding.periodObservationCount === "number" ? (
              <span className="tabular-nums text-muted-foreground">
                {finding.periodObservationCount} of {obsCount} in period
              </span>
            ) : (
              obsCount > 1 && (
                <span className="font-mono text-muted-foreground">
                  ({obsCount}×)
                </span>
              )
            )}
          </div>
        </div>

        {/* Right column: Trailing accessory (e.g. thumbnail) + Last Seen timestamp + Action buttons */}
        <div className="flex shrink-0 items-center gap-4 self-center">
          {trailing}
          {finding.lastSeenAt && (
            <Timestamp
              date={finding.lastSeenAt}
              className="w-16 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
            />
          )}
          {actions && (
            <div className="flex shrink-0 items-center justify-end gap-1.5">
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { FindingRow as FindingItem };
